import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik, FormikProvider, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  createStudent,
  updateStudent,
  setRedirect,
  getStudentDescription
} from '../../actions/student';
import SelectAlt from '../Input/SelectAlt';
import { BasicInput, CheckBox } from '../Input/index';
import { validateRut, createArrayYears, serializeJsonToString } from '../../services/utils';
import {
  setDataFormStudent,
  getYearStudent,
  setDataStudentRequest
} from './utils';
import API from '../../services/api';

const genderOptions = [
  { id: 'other', key: 1, title: 'Otro', option: 'other' },
  { id: 'female', key: 2, title: 'Femenino', option: 'female' },
  { id: 'male', key: 3, title: 'Masculino', option: 'male' }
];

const MAX_STUDENT_SECTIONS = 3;

const validationSchema = Yup.object({
  names: Yup.string()
  .min(3, 'Mínimo 3 caracteres')
  .required('Campo requerido'),
  last_names: Yup.string().required('Campo requerido'),
  rut: Yup.string()
  .min(9, 'Rut inválido')
  .required('Rut es un campo requerido'),
  sections_detail: Yup.lazy((value) => {
    const shape = {};
    Object.keys(value || {}).forEach((yearKey) => {
      shape[yearKey] = Yup.array().of(
        Yup.object().shape({
          section: Yup.mixed().test(
            'section-required',
            'Required',
            (val, context) => {
              const { establishment, grade } = context.parent;
              // Empty row (no data) is valid
              if (!establishment && !grade) return true;
              // Row with data needs a valid section
              return val && val > 0;
            }
          )
        })
      );
    });
    return Yup.object().shape(shape);
  })
});

const createInitialValues = () => {
  const currentYear = new Date().getFullYear();
  const years = createArrayYears(3, currentYear);

  const sections = {};

  for (let i = 0; i < years.length; i += 1) {
    const item = years[i];

    const formattedSection = [{
      year: item,
      establishment: 0,
      grade: 0,
      section: 0
    }]

    sections[item] = formattedSection;
  }

  return {
    id: '',
    names: '',
    last_names: '',
    rut: '',
    gender: '',
    active: true,
    description: 0,
    year: currentYear,
    establishment: '',
    establishment_id: 0,
    sections_detail: sections,
  };
};

const StudentForm = ({ data, onHide }) => {
  const [establishments, setEstablishments] = useState([]);
  const [isEstablishmentsLoading, setIsEstablishmentsLoading] = useState(false);

  const [grades, setGrades] = useState([]);
  const [isGradesLoading, setIsGradesLoading] = useState(false);

  const [sections, setSections] = useState({});
  const [isSectionsLoading, setIsSectionsLoading] = useState(false);

  const dispatch = useDispatch();

  const { ongoingRequest, oneStudent: student, redirect, studentDescription } = useSelector(
    state => state.student
  );

  const [years, setYears] = useState(() =>
    createArrayYears(3, 2020, new Date().getFullYear()).map((item, i) => ({
      key: i,
      id: item,
      title: item
    }))
  );

  const formik = useFormik({
    initialValues: createInitialValues(),
    validationSchema,
    enableReinitialize: false,
    onSubmit: values => {
      if (data.create) {
        dispatch(createStudent({ student: setDataStudentRequest(values) }));
      } else {
        dispatch(updateStudent({ student: setDataStudentRequest(values) }));
      }
    }
  });

  const { values, errors, touched, isValid, setFieldValue, setValues } = formik;

  const getEstablishments = async (params = {}) => {
    const serializedParams = serializeJsonToString(params);

    setIsEstablishmentsLoading(true)

    const response = await API.get(`/establishments?${serializedParams}`)

    const result = await response.json();

    setEstablishments(result.data);

    setIsEstablishmentsLoading(false)
  }

  const getGrades = async (params) => {
    const serializedParams = serializeJsonToString(params);

    setIsGradesLoading(true)

    const response = await API.get(`/grades?${serializedParams}`)

    const result = await response.json();

    setGrades(result.data.map((item) => ({...item, key: item.id, id: item.id, title: item.name})));

    setIsGradesLoading(false)
  }

  const getSections = async(params) => {
    const serializedParams = serializeJsonToString(params);

    setIsSectionsLoading(true)

    const response = await API.get(`/sections?${serializedParams}`)

    const result = await response.json();

    const newSections = {}

    const cacheKey = `${params.year}-${params.establishment_id}-${params.grade_id}`;

    newSections[cacheKey] = result.data.map((item) => ({...item, key: item.id, id: item.id, title: item.name}));

    setSections((prev) => ({...prev, ...newSections}))

    setIsSectionsLoading(false)
  }


  useEffect(() => {
    dispatch(getStudentDescription());
  }, [dispatch]);

  useEffect(() => {
    getEstablishments();
  }, [])

  useEffect(() => {
    if (!redirect.state) return;

    dispatch(setRedirect({ path: '', state: false }));
    onHide();
  }, [redirect.state, dispatch, onHide]);

  useEffect(() => {
    if (!ongoingRequest.getOneStudent && data.action === 'edit' && values.id === '') {
      const studentYears = getYearStudent(student);
      const studentSet = setDataFormStudent(student, studentYears);
      setYears(studentYears);

      setValues(studentSet);

      studentSet.sections_detail[values.year].forEach(section => {
        if (section.establishment) {
          getGrades({
            select: true,
            year: section.year,
            establishment_id: section.establishment
          })
        }

        if (section.grade) {
          getSections({
            year: section.year,
            establishment_id:section.establishment,
            grade_id: section.grade
          })
        }
      })
    }
  }, [
    ongoingRequest.getOneStudent,
    data.action,
    values.id,
    student,
    setValues,
  ]);

  // Handlers
  const onChangeRut = useCallback(
    value => {
      const formatRut = validateRut(value, values.rut);
      setFieldValue('rut', formatRut);
    },
    [setFieldValue, values.rut]
  );

  const onChangeSelects = useCallback((value, index, type) => {
      const state = Number(value) !== 0;
      const { sections_detail, year } = values;

      switch (type) {
        case 'year': {
          setFieldValue('year', value);
          if (sections_detail[value].length > 0) {
            for (let i = 0; i < sections_detail[value].length; i += 1) {
              if (sections_detail[value][i].establishment && sections_detail[value][i].grade) {
                getGrades({
                  select: true,
                  year: value,
                  establishment_id: sections_detail[value][i].establishment
                })
                getSections({
                  year: value,
                  establishment_id: sections_detail[value][i].establishment,
                  grade_id: sections_detail[value][i].grade
                })
              }
            }
          }

          break;
        }
        case 'establishment':
          if (state) {
            getGrades({
              select: true,
              year: sections_detail[year][index].year,
              establishment_id: value.value
            })
          }

          for (let i = 0; i < sections_detail[year].length; i += 1) {
            setFieldValue(`sections_detail[${year}][${i}].establishment`, value.value);
            setFieldValue(`sections_detail[${year}][${i}].establishmentName`, value.label);
            setFieldValue(`sections_detail[${year}][${i}].grade`, null);
          }
          break;
        case 'grade':
          if (!value) return;

          if (state) {
            getSections({
              year: sections_detail[year][index].year,
              establishment_id: sections_detail[year][index].establishment,
              grade_id: value
            })
          }
          setFieldValue(`sections_detail[${year}][${index}].grade`, value);
          setFieldValue(`sections_detail[${year}][${index}].section`, null);
          break;
        case 'section':
          setFieldValue(`sections_detail[${year}][${index}].section`, value);
          break;
        default:
          break;
      }
    },
    [dispatch, setFieldValue, values]
  );

  const stateDataselect = useCallback((selectData, onLoad, statePrevious) => {
    if (Number(statePrevious) === 0) {
      return [{ key: 0, title: 'Seleccione' }];
    }
    if (onLoad) {
      return [{ key: 0, title: 'Cargando..' }];
    }
    if (selectData.length === 1 && selectData[0].id === 0) {
      return [{ key: 0, title: 'Sin datos..' }];
    }
    return [{ key: null, title: 'Seleccione' }, ...selectData];
  }, []);

  const requestEstablishments = (requestData) => {
    getEstablishments({
      ...requestData,
      display_start: 0,
      display_length: 25,
      sort_direction: 'asce',
      role: 2
    })
  }

  const handleAddSection = () => {
    const newSection = {
      year: values.year,
      establishment: values.sections_detail[values.year][0].establishment,
      establishmentName: values.sections_detail[values.year][0].establishmentName,
      grade: null,
      section: 0,
      description: values.sections_detail[values.year][0].description,
    }

    const currentSections = values.sections_detail[values.year] || [];

    formik.setFieldValue(`sections_detail[${values.year}]`, [
      ...currentSections,
      newSection
    ]);
  }

  const handleRemoveSection = (indexToRemove) => {
    const currentSections = formik.values.sections_detail[values.year] || [];

    formik.setFieldValue(
      `sections_detail[${values.year}]`,
      currentSections.filter((_, index) => index !== indexToRemove)
    );
  }

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <Row className="inputRow">
          <Col xs={12} md={6}>
            <Field name="names">
              {({ field }) => (
                <BasicInput
                  {...field}
                  text={errors.names && touched.names && errors.names}
                  className={errors.names && touched.names ? 'is-invalid' : ''}
                  type="text"
                  title="Nombre"
                  placeholder="Escriba nombre"
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={6}>
            <Field name="last_names">
              {({ field }) => (
                <BasicInput
                  {...field}
                  text={errors.last_names && touched.last_names && errors.last_names}
                  className={errors.last_names && touched.last_names ? 'is-invalid' : ''}
                  type="text"
                  title="Apellido"
                  placeholder="Escriba apellido"
                />
              )}
            </Field>
          </Col>
        </Row>

        <Row className="inputRow">
          <Col xs={12} md={6}>
            <Field name="rut">
              {({ field }) => (
                <BasicInput
                  {...field}
                  className={errors.rut && touched.rut ? 'is-invalid' : ''}
                  text={errors.rut && touched.rut && errors.rut}
                  onChange={e => onChangeRut(e.target.value)}
                  type="text"
                  title="RUT"
                  placeholder="9.999.999-9"
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={6}>
            <Field name="gender">
              {() => (
                <SelectAlt
                  data={genderOptions}
                  value={values.gender}
                  title="Genero"
                  onChangeSelect={value => setFieldValue('gender', value.value)}
                />
              )}
            </Field>
          </Col>
        </Row>

        <Row className="inputRow">
          <Col xs={12} md={12}>
            <Field name="description">
              {() => (
                <SelectAlt
                  data={studentDescription}
                  value={values.description || 'no_description'}
                  title="Descripción"
                  onChangeSelect={value => setFieldValue('description', value.value)}
                />
              )}
            </Field>
          </Col>
        </Row>

        <Row>
          <Col xs={5}>
            <Field name="active">
              {({ field, form }) => (
                <CheckBox
                  {...field}
                  custom
                  controlId="asd"
                  onChange={e => form.setFieldValue('active', !e.target.checked)}
                  label="Congelar alumno"
                  checked={!values.active}
                />
              )}
            </Field>
          </Col>
        </Row>

        <hr />

        <Row className="inputRow">
          <Col>
            <Field name="year">
              {() => (
                <SelectAlt
                  data={years}
                  value={values.year}
                  title="Año"
                  onChangeSelect={value => {
                    onChangeSelects(value.value, null, 'year');
                  }}
                />
              )}
            </Field>
          </Col>
        </Row>

        <FieldArray name="sections_detail">
          <Row>
            <Col>
              {values.sections_detail[values.year].map((section, index) => {
                const establishmentId = values.sections_detail[values.year][index].establishment;
                const gradeId = values.sections_detail[values.year][index].grade;

                const sectionGrade = grades.find(
                  grade => grade.id === values.sections_detail[values.year][index].grade
                );

                const sectionsCacheKey = `${values.year}-${establishmentId}-${gradeId}`;

                const currentSection = (sections[sectionsCacheKey] || []).find(
                  sectionx => sectionx.id === values.sections_detail[values.year][index].section
                );

                return (
                  <div>
                    <Row className="mb-4">
                      <Col>
                        <Field name={`sections_detail[${values.year}][${index}].establishment`}>
                          {field => (
                            <SelectAlt
                              {...field}
                              placeholder="Seleccione"
                              data={establishments}
                              initialValue={
                                values.sections_detail[values.year][index].establishment
                                  ? {
                                    value: values.sections_detail[values.year][index].establishment,
                                    label: values.sections_detail[values.year][index].establishmentName
                                  }
                                  : { value: null, label: 'Seleccione' }
                              }
                              server
                              isSearchable
                              dispatchRequest={requestEstablishments}
                              onChangeSelect={e => {
                                onChangeSelects(e, index, 'establishment');
                              }}
                              onLoad={isEstablishmentsLoading}
                              disabled={index >= 1}
                              title={
                                <span className="d-flex justify-content-between align-items-center">
                                  <span>Establecimiento</span>
                                  <Button disabled={values.sections_detail[values.year].length === 1} variant="danger" className="btn-sm" onClick={() => handleRemoveSection(index)}>
                                    Borrar
                                  </Button>
                                </span>
                              }
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Field name={`sections_detail[${values.year}][{${index}}].grade`}>
                          {() => (
                            <SelectAlt
                              title="Curso"
                              initialValue={
                                sectionGrade
                                  ? {
                                    value: sectionGrade.id,
                                    label: sectionGrade.name
                                  }
                                  : { value: null, label: 'Seleccione' }
                              }
                              server
                              dispatchRequest={() => true}
                              onChangeSelect={e => onChangeSelects(e.value, index, 'grade')}
                              data={stateDataselect(
                                grades,
                                isGradesLoading,
                                values.sections_detail[values.year][index].establishment
                              )}
                              disabled={Number(values.sections_detail[values.year][index].establishment) === 0}
                            />
                          )}
                        </Field>
                      </Col>
                      <Col xs={12} md={6}>
                        <Field name={`sections_detail[${values.year}][${index}].section`}>
                          {() => (
                            <SelectAlt
                              title="Sección"
                              server
                              dispatchRequest={() => true}
                              initialValue={
                                currentSection
                                  ? {
                                    value: currentSection.id,
                                    label: currentSection.name
                                  }
                                  : { value: null, label: 'Seleccione' }
                              }
                              onChangeSelect={e => onChangeSelects(e.value, index, 'section')}
                              data={stateDataselect(
                                (sections[sectionsCacheKey] || []),
                                isSectionsLoading,
                                values.sections_detail[values.year][index].grade
                              )}
                              disabled={Number(values.sections_detail[values.year][index].grade) === 0}
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>
                  </div>
                )
              })}
            </Col>
          </Row>
        </FieldArray>

        <Row>
          <Col className="mb-3">
            <Button
              className="w-100"
              variant="outline-secondary"
              onClick={handleAddSection}
              disabled={values.sections_detail[values.year].length >= MAX_STUDENT_SECTIONS}
            >
              Añadir sección
            </Button>
          </Col>
        </Row>

        <hr/>

        <Row>
          <Col md={{ span: 3, offset: 6 }}>
            <Button onClick={onHide} block variant="light">
              Cancelar
            </Button>
          </Col>
          <Col md={3}>
            <Button type="submit" disabled={!isValid} block>
              {data.create ? 'Crear' : 'Guardar'}
            </Button>
          </Col>
        </Row>
      </form>
    </FormikProvider>
  );
};

export default StudentForm;
